import moment from 'moment-timezone';

import { getSessionKind, RegularSessionBounds, SessionKind } from '../../CanvasChart/sessionSegments';

export type { RegularSessionBounds, SessionKind };

export const DAILY_CHART_INTERVAL_MINUTES = 5;
const MINUTES_IN_HOUR = 60;

// 5-minute slots counted from midnight, exchange time
export type DailyChartSession = {
  timeZone: string;
  startSlot: number;
  endSlot: number;
};

export type TimeOfDayString = string | null | undefined;

export type DailySessionTimes = {
  timeZone?: string | null;
  start?: TimeOfDayString;
  end?: TimeOfDayString;
};

export type DailySessionDefaults = {
  timeZone: string;
  start: string;
  end: string;
};

export type MarketHoursLike = {
  open?: TimeOfDayString;
  close?: TimeOfDayString;
  closeForHalfDay?: TimeOfDayString;
};

export type NonWorkingDayLike = {
  date: string;
  halfDay?: boolean;
};

export type RegularSessionOptions = {
  nonWorkingDays?: NonWorkingDayLike[];
  dateAndTime?: string | null;
  defaults: { open: string; close: string };
};

export type DailyAxisLabelOptions = {
  labelIntervalMinutes: number;
  labelEdgeSlots: boolean;
};

export type DatedCandle = {
  dateAndTime?: string | null;
  date?: string | null;
  time?: string | null;
};

export const parseTimeToSlot = (value: TimeOfDayString): number | null => {
  if (!value) return null;

  const [ hourStr, minuteStr ] = value.split(':');
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  return Math.round(((hour * MINUTES_IN_HOUR) + minute) / DAILY_CHART_INTERVAL_MINUTES);
};

export const timeToSlot = (value: TimeOfDayString, fallback: string): number => (
  parseTimeToSlot(value) ?? parseTimeToSlot(fallback)!
);

export const buildDailyChartSession = (
  times: DailySessionTimes,
  defaults: DailySessionDefaults,
): DailyChartSession => ({
  timeZone: times.timeZone || defaults.timeZone,
  startSlot: timeToSlot(times.start, defaults.start),
  endSlot: timeToSlot(times.end, defaults.end),
});

export const getDailySessionLength = (session: DailyChartSession): number => session.endSlot - session.startSlot;

export const getRegularSessionBounds = (
  session: DailyChartSession,
  marketHours: MarketHoursLike,
  options: RegularSessionOptions,
): RegularSessionBounds => {
  const { nonWorkingDays = [], dateAndTime, defaults } = options;
  const dateString = dateAndTime ? moment.utc(dateAndTime).tz(session.timeZone).format('YYYY-MM-DD') : null;
  const isHalfDay = nonWorkingDays.some(day => day.date === dateString && day.halfDay);
  const closeTime = isHalfDay ? marketHours.closeForHalfDay : marketHours.close;
  const lastIndex = getDailySessionLength(session);
  const clamp = (index: number) => Math.max(0, Math.min(index, lastIndex));

  return {
    startIndex: clamp(timeToSlot(marketHours.open, defaults.open) - session.startSlot),
    endIndex: clamp(timeToSlot(closeTime, defaults.close) - session.startSlot),
  };
};

export type SessionKindContext = RegularSessionOptions & { marketHours: MarketHoursLike };

export const getSessionKindForDateAndTime = (
  dateAndTime: string | null | undefined,
  session: DailyChartSession,
  context: SessionKindContext,
): SessionKind | null => {
  const index = getDailyChartIndex(dateAndTime, session);
  if (index === null) return null;

  const { marketHours, ...options } = context;
  const bounds = getRegularSessionBounds(session, marketHours, { ...options, dateAndTime });

  return getSessionKind(index, bounds);
};

export const getOHLCVDateAndTime = (item: DatedCandle): string | null => (
  item.dateAndTime ?? (item.date && item.time ? `${item.date}T${item.time}` : null)
);

// Rows are not guaranteed to be sorted or to share one date-time format
export const getLatestDateAndTime = (data: DatedCandle[]): string | null => {
  const latest = data.reduce<{ value: string; time: number } | null>((best, item) => {
    const value = getOHLCVDateAndTime(item);
    if (!value) return best;

    const time = moment.utc(value).valueOf();
    if (Number.isNaN(time) || (best !== null && time <= best.time)) return best;

    return { value, time };
  }, null);

  return latest?.value ?? null;
};

export const getDailyChartIndex = (
  dateAndTime: string | null | undefined,
  session: DailyChartSession,
): number | null => {
  if (!dateAndTime) return null;

  const exchangeTime = moment.utc(dateAndTime).tz(session.timeZone);
  if (!exchangeTime.isValid()) return null;

  const minutesFromMidnight = (exchangeTime.hours() * MINUTES_IN_HOUR) + exchangeTime.minutes();
  const slot = Math.round(minutesFromMidnight / DAILY_CHART_INTERVAL_MINUTES);
  const index = slot - session.startSlot;

  return index >= 0 && slot <= session.endSlot ? index : null;
};

const formatSlotAsLocalTime = (slot: number, timeZone: string): string => {
  const minutesFromMidnight = slot * DAILY_CHART_INTERVAL_MINUTES;

  return moment.tz(timeZone)
    .hour(Math.floor(minutesFromMidnight / MINUTES_IN_HOUR))
    .minute(minutesFromMidnight % MINUTES_IN_HOUR)
    .second(0)
    .local()
    .format('HH:mm');
};

export const dailyAxisValues = (session: DailyChartSession, options: DailyAxisLabelOptions): string[] => {
  const values: string[] = [];

  for (let slot = session.startSlot; slot <= session.endSlot; slot += 1) {
    const isEdgeSlot = slot === session.startSlot || slot === session.endSlot;
    const isIntervalSlot = (slot * DAILY_CHART_INTERVAL_MINUTES) % options.labelIntervalMinutes === 0;
    const isLabelSlot = options.labelEdgeSlots ? isIntervalSlot || isEdgeSlot : isIntervalSlot && !isEdgeSlot;
    values.push(isLabelSlot ? formatSlotAsLocalTime(slot, session.timeZone) : '');
  }

  return values;
};
